import React, { FC, useState, useEffect, useRef } from 'react'; 
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, IconButton } from '@mui/material';
import { GoogleMap, LoadScript, Marker, useJsApiLoader } from '@react-google-maps/api';
import {
  setKey,
  setLanguage,
  setRegion,
  setLocationType,
  fromLatLng,
  fromAddress
} from 'react-geocode';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import Heading from '../../Components/Heading/heading.tsx';

setKey("AIzaSyA5izcJ7TGIrfklTCrMx3Q2JmeK_zld1sc");
setLanguage("en");
setRegion("in");
setLocationType("ROOFTOP");

interface MapModalProps {
  open: boolean;
  onClose: () => void;
  onLocationSelect: (address: string, lat: string, lng: string) => void;
  initialLocation?: { lat: number; lng: number };
}

const MapModal: FC<MapModalProps> = ({ open, onClose, onLocationSelect, initialLocation }) => {
  const DEFAULT_LOCATION = { lat: 12.9716, lng: 77.5946 };
  
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(
    initialLocation || null
  );
  const [address, setAddress] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);

const { isLoaded } = useJsApiLoader({
  id: 'google-map-script',
  googleMapsApiKey: "AIzaSyA5izcJ7TGIrfklTCrMx3Q2JmeK_zld1sc",
  libraries: ["places"], 
});


  useEffect(() => {
      if (!open) return;
    const setInitialLocation = async () => {
      if (initialLocation) {
        setSelectedLocation(initialLocation);
        await reverseGeocode(initialLocation.lat, initialLocation.lng);
        return;
      }
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            setCurrentLocation({ lat: latitude, lng: longitude });
            setSelectedLocation({ lat: latitude, lng: longitude });
            await reverseGeocode(latitude, longitude);
          },
          (error) => {
            setSelectedLocation(DEFAULT_LOCATION);
            reverseGeocode(DEFAULT_LOCATION.lat, DEFAULT_LOCATION.lng);
          }
        );
      } else {
        setSelectedLocation(DEFAULT_LOCATION);
        reverseGeocode(DEFAULT_LOCATION.lat, DEFAULT_LOCATION.lng);
      }
    };

    setInitialLocation();
  }, [initialLocation]);

  const reverseGeocode = async (lat: number, lng: number) => {
    try {
      setIsLoading(true);
      const response = await fromLatLng(lat.toString(), lng.toString());
      const formattedAddress = response.results[0].formatted_address;
      setAddress(formattedAddress);
    } catch (error) {
      setAddress("Address not found");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMapClick = async (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setSelectedLocation({ lat, lng });
      await reverseGeocode(lat, lng);
    }
  };
const mapRef = useRef<google.maps.Map | null>(null);
const handleSearch = () => {
  if (!searchQuery.trim()) return;
  if (!selectedLocation) return;

  const service = new google.maps.places.PlacesService(mapRef?.current!);
  const request: google.maps.places.FindPlaceFromQueryRequest = {
    query: searchQuery,
    fields: ["name", "geometry", "formatted_address"],
  };

  service.findPlaceFromQuery(request, (results, status) => {
    if (status === google.maps.places.PlacesServiceStatus.OK && results && results[0]) {
      const place = results[0];
      
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setSelectedLocation({ lat, lng });
        setAddress(place.formatted_address || place.name || "");
        mapRef.current?.panTo({ lat, lng });
      } else {
        setAddress("Location found, but coordinates not available");
      }

    } else {
      setAddress("Location not found");
    }
  });
};




  const handleCurrentLocation = () => {
    if (currentLocation) {
      setSelectedLocation(currentLocation);
      reverseGeocode(currentLocation.lat, currentLocation.lng);
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const loc = { lat: latitude, lng: longitude };
          setCurrentLocation(loc);
          setSelectedLocation(loc);
          await reverseGeocode(latitude, longitude);
        },
        (error) => {
          console.error("Error getting current location:", error);
        }
      );
    }
  };

  const handleConfirm = () => {
    if (selectedLocation && address) {
      onLocationSelect(
        address,
        selectedLocation.lat.toString(),
        selectedLocation.lng.toString()
      );
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <Box sx={{marginLeft: {xs: '0px',sm: '-180px',},}}>    
        <Heading title="Select Location On Map" closeIcon="true" onClose={onClose} />
      </Box>
      <DialogContent style={{ height: '500px', padding: 0 }}>
        <div style={{ padding: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <TextField
            label="Search for a place"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            disabled={isLoading}
            size="medium"
            style={{ flex: 1 }} 
          />
          <Button 
            variant="contained"
            className='custom-button'
            onClick={handleSearch}
            disabled={isLoading || !searchQuery.trim()}
          >
            Search
          </Button>
          <IconButton 
            onClick={handleCurrentLocation}
            title="Go to current location"
            color="primary"
          >
            <MyLocationIcon />
          </IconButton>
        </div>

        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '400px' }}
            center={selectedLocation || DEFAULT_LOCATION}
            zoom={15}
            onClick={handleMapClick}
            onLoad={map => (mapRef.current = map)}
          >

            {selectedLocation && <Marker position={selectedLocation} />}
            {currentLocation && (
              <Marker 
                position={currentLocation} 
                icon={{
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 7,
                  fillColor: '#4285F4',
                  fillOpacity: 1,
                  strokeWeight: 2,
                  strokeColor: 'white'
                }}
              />
            )}
          </GoogleMap>
        ) : (
          <div>Loading map...</div>
        )}
        <div style={{ padding: '16px' }}>
          <TextField
            fullWidth
            label="Selected Address"
            value={address}
            InputProps={{
              readOnly: true,
            }}
            helperText={isLoading ? "Loading..." : "Click on the map to select a location or search above"}
          />
        </div>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'center' }}>
        <Button
          onClick={onClose}
          className="custom-cancel-button"
          style={{ marginRight: '10px' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm} 
          className="custom-button"
          style={{ marginLeft: '10px' }}
        >
          Confirm Location
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapModal;